import { WizardRecomendedControlsFormValues } from 'src/api/types/wizard';
import _ from 'lodash';
import { ControlsRmpQuery, FrameworkControlScenarioInput } from 'src/graphql/_generated';

export const SLIDE_HEIGHT = 400;

export const initialValues = {
  scenarios: {
    B2B: false,
    B2C: false,
    publiclyAccessibleInternetBusiness: false,
    paymentVolumeOrValue: false,
    significantITContributionToRevenue: false,
    significantDataInCloud: false,
    softwareBusiness: false,
    cniOrDefenseInvolvement: false,
    significantRandD: false,
    regulated: false,
    operationalTechnology: false,
    supplyChainReliant: false,
  },
  selectedControls: {} as Record<string, Record<string, boolean>>, // controlId -> scenarioId -> isSelected
  addRecommendedModifiers: false,
} satisfies WizardRecomendedControlsFormValues;

export const getControlId = (control: any): string => {
  // return control.controlLibrary_Id || control.id || control.ux_id;
  return control.id || control.ux_id;
};

export const hasNewSelectedControls = (
  selectedControls: Record<string, Record<string, boolean>>,
  rmpControls: ControlsRmpQuery['controls'],
): boolean => {
  if (_.isEmpty(selectedControls)) return false;

  const existingLinkages = new Map<string, Set<string>>();

  rmpControls.forEach((control) => {
    const controlId = control.frameworkControls[0]?.id || control.frameworkControls[0]?.control_identifier;
    if (!controlId) return;

    const linkedScenarioIds = control.riskManagementPlans.map((rmp) => rmp.scenario?.id).filter(Boolean) as string[];

    existingLinkages.set(controlId, new Set(linkedScenarioIds));

    const altControlId = control.frameworkControls[0]?.control_identifier;
    if (altControlId && altControlId !== controlId) {
      existingLinkages.set(altControlId, new Set(linkedScenarioIds));
    }
  });

  for (const [controlId, scenarios] of Object.entries(selectedControls)) {
    const existingScenarios = existingLinkages.get(controlId) || new Set();

    for (const [scenarioId, isSelected] of Object.entries(scenarios)) {
      if (isSelected && !existingScenarios.has(scenarioId)) {
        return true;
      }
    }
  }

  return false;
};

type ControlScenarioGroup = {
  controlId: string;
  scenarioIds: string[];
};

type GetControlScenarioGroups = (params: {
  controls: ControlsRmpQuery['controls'];
  frameworkControlScenarioLinks: FrameworkControlScenarioInput[];
  selectedControls: Record<string, Record<string, boolean>>;
}) => {
  existingRmpScenarioGroups: ControlScenarioGroup[];
  newControlScenarioGroups: ControlScenarioGroup[];
};

export const getControlScenarioGroups: GetControlScenarioGroups = ({
  controls,
  frameworkControlScenarioLinks,
  selectedControls,
}) => {
  const existingRmpScenarioGroups: ControlScenarioGroup[] = [];
  const newControlScenarioGroups: ControlScenarioGroup[] = [];

  // Get existing RMP control IDs
  const existingControlIds = new Set(controls.map((control) => control.id));

  // Process existing RMP controls
  for (const control of controls) {
    const scenarioIds: string[] = [];

    // Find scenarios from frameworkControlScenarioLinks that match this control
    const matchingLinks = frameworkControlScenarioLinks.filter((link) => link.frameworkControlId === control.id);

    // Add scenario IDs from frameworkControlScenarioLinks
    for (const link of matchingLinks) {
      if (!scenarioIds.includes(link.scenarioId)) {
        scenarioIds.push(link.scenarioId);
      }
    }

    // Also check selectedControls for additional scenarios
    const selectedControl = selectedControls[control.id];
    if (selectedControl) {
      for (const [scenarioId, isSelected] of Object.entries(selectedControl)) {
        if (isSelected && !scenarioIds.includes(scenarioId)) {
          scenarioIds.push(scenarioId);
        }
      }
    }

    // Only add to existingRmpScenarioGroups if there are scenarios to link
    if (scenarioIds.length > 0) {
      existingRmpScenarioGroups.push({
        controlId: control.id,
        scenarioIds,
      });
    }
  }

  // Process new controls (not in existing RMP)
  const frameworkControlsMap = new Map<string, string[]>();

  for (const link of frameworkControlScenarioLinks) {
    if (!existingControlIds.has(link.frameworkControlId)) {
      if (!frameworkControlsMap.has(link.frameworkControlId)) {
        frameworkControlsMap.set(link.frameworkControlId, []);
      }
      frameworkControlsMap.get(link.frameworkControlId)!.push(link.scenarioId);
    }
  }

  // Also check selectedControls for additional new controls
  for (const [controlId, scenarios] of Object.entries(selectedControls)) {
    if (!existingControlIds.has(controlId)) {
      const scenarioIds = frameworkControlsMap.get(controlId) || [];

      for (const [scenarioId, isSelected] of Object.entries(scenarios)) {
        if (isSelected && !scenarioIds.includes(scenarioId)) {
          scenarioIds.push(scenarioId);
        }
      }

      if (scenarioIds.length > 0) {
        frameworkControlsMap.set(controlId, scenarioIds);
      }
    }
  }

  // Convert map to array format for new controls
  for (const [controlId, scenarioIds] of frameworkControlsMap.entries()) {
    if (scenarioIds.length > 0) {
      newControlScenarioGroups.push({
        controlId,
        scenarioIds,
      });
    }
  }

  return {
    existingRmpScenarioGroups,
    newControlScenarioGroups,
  };
};
